import React, { useEffect, useCallback, useState} from 'react';
import SimpleSequencerBottomToolbar from 'components/installation/SimpleSequencerBottomToolbar.js';
import TrackQuickView from 'components/sidebar/TrackQuickView.js';
import ethereum from 'decentralized/ethereum.js';
import EthereumCache from 'decentralized/cache/EthereumCache.js';
import MiniTrackComponent from './MiniTrackComponent.js';
import EthereumSoundsSearcher from 'search/EthereumSoundsSearcher.js';
import SidebarComponent from 'components/sidebar/SidebarComponent.js';
import DocLink from './DocLink.js';
import {connect} from 'react-redux';

const ethereumCache = new EthereumCache();

const GettingStarted = ({goToLink, sequencerEngine, loadingProgress, showPianoRoll}) => { 
    const [searcher, setSearcher] = useState(new EthereumSoundsSearcher(
        ethereumCache, sequencerEngine));

    useEffect(() => {
        ethereum.cache = ethereumCache;
        ethereum.cache.loadIndex();
    }, []);

    useEffect(() => {
        if (loadingProgress === 100 && sequencerEngine.subSequences.length === 2) {
            sequencerEngine.newSubSequence();
            // we're loaded so lets create a new sub sequence
        } else if (loadingProgress === 100 && sequencerEngine.subSequences.length === 3) {
            sequencerEngine.switchToSubSequence(sequencerEngine.subSequences[2].id);
        }
        return () => {
            if (sequencerEngine.subSequences[0]) {
                sequencerEngine.switchToSubSequence(sequencerEngine.subSequences[0].id);
            }
        };
    }, [loadingProgress]);
    
    let tracks = '';
    if (sequencerEngine.tracks && sequencerEngine.tracks.length > 0) {
        tracks = (
            <div className="getting-started">
              <MiniTrackComponent sequencerEngine={sequencerEngine}/>
              <p className="sub-header">
                Browsing Sounds
              </p>
              <p>
                You can use the arrow buttons in the sidebar-- shown below-- to audition different sounds 
                for the currently selected track.
              </p>
              <p>
                For example, you can browse through hi-hat sounds until you've found the sound you want.
              </p>
              <div className="two-column">
                <div className="text-column">
                  <p>
                    The component to the right (also seen in the sidebar) allows you to change the track-wide
                    settings for the selected track.
                  </p>
                  <p>
                    For example, you can change the pitch of the track's triggers by clicking the <b>Trsp</b> (short for transpose) button
                    and using the slider to change the pitch.
                  </p>
                </div>
                <div className="text-column">
                  <TrackQuickView
                    installation={true}
                    sequencerEngine={sequencerEngine}/>
                </div>
              </div>
          </div>
        );
    }
 
    return (
        <div>
          <p>
            Zequencer is a DAW, or Digital Audio Workstation. That's a fancy word for "music making tool".
            It can sequence sounds in cool ways, with your help.
          </p>
          <p>
            The <a href="/create" target="_blank">create</a> page is where you 
            create music with Zequencer. 
          </p>
          <p>
            You'll be greeted by an empty project. Let's breakdown the different parts of this screen.
          </p>
          <p className="sub-header">
            Sidebar
          </p>
          <p>
            You choose your sounds and instruments here. The sidebar contains a few buttons that let you quickly
            add sounds to your project.
          </p>
          <div className="interactive-view">
            <div>
              <p>
                These buttons represent the different parts of a drum kit, along with some other
                frequently used sounds.
              </p>
              <p>
                Clicking the <b>Kick</b>, <b>Snare</b>, <b>Hi-Hat</b>, <b>Clap</b> buttons adds a random
                sound of that drum type to the project.               </p> 
              <p>
                Clicking the <b>Synth</b> button adds a new synthesizer to the project. 
              </p> 
              <p>
                <b>Try adding a few drum sounds here by clicking the <b>Hi-hat</b> button!</b>
              </p>
              <p className="sub-header">
                Tracks
              </p>
              <p>
                As you add sounds and synths to the project, they will appear to the left of the sidebar.
                These tracks are where you will sequence the sounds. Look
                below to see how these tracks look like. 
              </p>
              <p>
                Learn more about editing patterns on the <DocLink name="Step Sequencer" goToLink={goToLink}/> page.
              </p>
            </div>
            <SidebarComponent
              hidePresets={true}
              disableStickiness={true} hideTrack={tracks === ''} sequencerEngine={sequencerEngine} ethereumSearcher={searcher}/>
          </div>

          {tracks}
       </div>);
}

const mapStateToProps = (state, {id, sequencerEngine}) => {
    return {
        loadingProgress: state.sequencer.sequenceLoadingProgress[id || sequencerEngine.sequenceId] || 0,
        tracks: sequencerEngine.tracks && sequencerEngine.tracks.length,
        showPianoRoll: state.sequencer.showPianoRoll,
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(GettingStarted);
